/**
 * The layer-build worker cap (app/layerstack/drain.py). Workers scale
 * between 0 and the cap with the queue, so a high cap costs nothing while
 * idle; the first build after idle waits for a container to start.
 * Platform admins change it (the server enforces that); everyone with the
 * Builds board sees it. Outside drain mode the cap is stored but inert,
 * and the line says so.
 */
import { useCallback, useEffect, useState } from 'react'

import { ApiError, api } from '../lib/api'

export interface WorkersState {
  mode: string
  max: number
  starting: number
  idle: number
  busy: number
  queued_games: number
  min: number
  max_allowed: number
}

const PATH = '/api/v1/opsqa/build-workers'

export function BuildWorkers({ canEdit, refreshKey }: { canEdit: boolean; refreshKey: number }) {
  const [st, setSt] = useState<WorkersState | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    api<WorkersState>(PATH).then(setSt).catch(() => {})
  }, [])
  useEffect(load, [load, refreshKey])

  if (!st) return null
  const live = st.starting + st.idle + st.busy
  const drain = st.mode === 'drain'

  const set = (n: number) => {
    setSaving(true)
    setError(null)
    api<WorkersState>(PATH, { method: 'PUT', body: JSON.stringify({ max: n }) })
      .then(setSt)
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : String(e)))
      .finally(() => setSaving(false))
  }

  const options = Array.from({ length: st.max_allowed - st.min + 1 }, (_, i) => st.min + i)
  return (
    <span data-testid="build-workers">
      {drain
        ? <><strong>{live}</strong> of {st.max} worker{st.max === 1 ? '' : 's'} up
            {st.starting > 0 && ` (${st.starting} starting)`}</>
        : 'Workers: always on'}
      {' · cap '}
      {canEdit
        ? <select aria-label="Build worker cap" value={st.max} disabled={saving}
                  onChange={(e) => set(Number(e.target.value))}>
            {options.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        : <strong>{st.max}</strong>}
      {!drain && <span className="muted"> (has no effect until scale-to-zero is on)</span>}
      {error && <span className="error"> {error}</span>}
    </span>
  )
}
